import { quat, glMatrix, mat4, vec2 } from '../math/gl-matrix';
import sign from '../math/sign';

export const TAU = Math.PI * 2;
export const DEG = Math.PI / 180;
export const IDENTITY_MATRIX = mat4.create();
export const DEFAULT_ORIGIN_X = 0.5;
export const DEFAULT_ORIGIN_Y = 0.5;

export function identityTransformProps() {
  return {
    position: [DEFAULT_ORIGIN_X, DEFAULT_ORIGIN_Y],
    rotation: 0,
    scale: 1,
    bounds: { x: 0, y: 0, width: 1, height: 1 },
    flipX: false,
    flipY: false,
  };
}

export function getTransformProps(obj) {
  let props = identityTransformProps();
  if (!obj) return props; // TODO; this sometimes occurs after deleting a layer. possibly a race condition for data
  if (obj.rotation !== undefined) props.rotation = obj.rotation;
  if (obj.position?.length) props.position = obj.position.slice(0, 2);
  if (obj.scale) props.scale = obj.scale;
  if (obj.flipY) props.flipY = obj.flipY;
  if (obj.flipX) props.flipX = obj.flipX;
  if (obj.bounds) {
    let { x, y, width, height } = obj.bounds;
    props.bounds = { x, y, width, height };
  }
  return props;
}

/**
 * Perform an operation on a transform matrix
 * while respecting an origin point at 0.5, 0.5
 * @param matrix - The matrix for calculation
 * @param originX - The origin X point to translate from
 * @param originY - The origin Y point to translate from
 * @param fn - The transformation method
 */
export function executeWithOrigin(matrix, originX, originY, fn) {
  mat4.translate(matrix, matrix, [originX, originY, 0]);
  fn();
  mat4.translate(matrix, matrix, [-originX, -originY, 0]);
}

/**
 * Returns a new matrix based on translating the
 * existing one by a provided degree
 * @param matrix - The matrix for calculation
 * @param degree - The degree of rotation
 * @param originX - The origin X point to rotate from
 * @param originY - The origin Y point to rotate from
 * @returns rotatedMatrix
 */
export function rotateMatrix(
  matrix,
  degree,
  originX = DEFAULT_ORIGIN_X,
  originY = DEFAULT_ORIGIN_Y
) {
  let rotatedMatrix = mat4.clone(matrix);
  executeWithOrigin(rotatedMatrix, originX, originY, () => {
    mat4.rotateZ(rotatedMatrix, rotatedMatrix, glMatrix.toRadian(degree));
  });
  return rotatedMatrix;
}

/**
 * Returns a new matrix scaled at the specified factor.
 * @param matrix - The matrix for calculation
 * @param scaleFactor - The value to scale by
 * @param originX - The origin X point to scale from
 * @param originY - The origin Y point to scale from
 * @returns scaledMatrix
 */
export function setScaleMatrix(
  matrix,
  scaleFactor,
  originX = DEFAULT_ORIGIN_X,
  originY = DEFAULT_ORIGIN_Y
) {
  let scaledMatrix = mat4.clone(matrix);
  const relativeScaleFactor = scaleFactor / getScale(matrix);

  executeWithOrigin(scaledMatrix, originX, originY, () => {
    mat4.scale(scaledMatrix, scaledMatrix, [relativeScaleFactor, relativeScaleFactor, 1]);
  });
  return scaledMatrix;
}

/**
 * Get the rotation value from the matrix in degrees
 * @param matrix - The matrix to extract the value from
 * @returns radians or degrees
 */
export function getRotation(matrix, returnRadians) {
  // We have to remove any scale factor for our computation to work
  let unscaledMatrix = mat4.clone(matrix);
  const scaleFactor = 1 / getScale(matrix);
  mat4.scale(unscaledMatrix, matrix, [scaleFactor, scaleFactor, 1]);

  let quaternion = quat.create();
  mat4.getRotation(quaternion, unscaledMatrix);

  let vector = [0, 0, 1];
  let radians = quat.getAxisAngle(vector, quaternion) || 0;
  let flip = sign(vector[2]) === -1;
  if (returnRadians) {
    return flip ? 2 * Math.PI - radians : radians;
  } else {
    let degrees = Math.round((radians * 180) / Math.PI);
    return flip ? 360 - degrees : degrees;
  }
}

export function cloneAndUnpack(matrix) {
  let cloneTransform = mat4.clone(matrix);
  mat4.invert(cloneTransform, cloneTransform);
  const rotation = getRotation(cloneTransform);
  const scale = getScale(cloneTransform);
  cloneTransform = setScaleMatrix(cloneTransform, 1);
  cloneTransform = rotateMatrix(cloneTransform, -rotation);
  return {
    x: cloneTransform[12],
    y: cloneTransform[13],
    rotation, // because we need this information to get the transformation we return it
    scale,
    transform: cloneTransform,
  };
}

/**
 * Get the scale value from the matrix as a percentage
 * @param matrix - The matrix to extract the value from
 * @returns scaleFactor
 */
export function getScale(matrix) {
  let [m11, m12, m13] = matrix.slice(0, 3);
  return Math.sqrt(m11 * m11 + m12 * m12 + m13 * m13);
}

export function copyMatrix(matrix) {
  return mat4.clone(matrix);
}

export function getGlobalRect({ bounds, ...transform }, gRatio = 1) {
  const { width: w, height: h } = bounds;
  let [left, top] = localToGlobal(transform, [-w / 2, -h / 2], gRatio);
  let [right, bottom] = localToGlobal(transform, [w / 2, h / 2], gRatio);
  let [minx, maxx] = [Math.min(left, right), Math.max(left, right)];
  let [miny, maxy] = [Math.min(top, bottom), Math.max(top, bottom)];
  return [minx, miny, maxx, maxy];
}

/**
 * Returns updated position and scale for a layer so that it cover's a give layer
 * @param {*} layer layer data
 * @param {*} target constraining layer data
 * @param {*} gRatio the aspect ratio of the scene
 * @returns
 */
export function coverRect(layer, target, gRatio = 1) {
  target = target || identityTransformProps();
  let { width: w, height: h } = layer?.bounds || { width: 1, height: 1 };
  // get the bounding box of `target` projected into `layer`
  let box = projectBox(target, layer, gRatio);
  // calculate a scale factor based on the target box, should be at least 1
  let scale = Math.max(1, box[0] / w, box[1] / h);
  // get target's center within the local layer coordinates
  let cpos = globalToLocal(layer, target.position, gRatio);
  // calc range of movement around target center
  let offsetX = (w - box[0] / scale) / 2;
  let offsetY = (h - box[1] / scale) / 2;
  // apply range constraint to target center
  let npos = [
    Math.min(Math.max(-offsetX, cpos[0]), offsetX),
    Math.min(Math.max(-offsetY, cpos[1]), offsetY),
  ];

  // the final position is the difference between original and constrained in global coordinates
  let position = localToGlobal(layer, vec2.sub([], cpos, npos), gRatio);
  return { position, scale: layer.scale * scale };
}

/**
 * Returns the bounding box of a layer projected into another
 * @param {*} layer layer data
 * @param {*} target target layer data
 * @param {*} gRatio the aspect ratio of the scene
 * @returns
 */
export function projectBox(layer, target, gRatio = 1) {
  layer = layer || identityTransformProps();
  let { width: w, height: h } = layer?.bounds || { width: 1, height: 1 };
  if (typeof w === 'string') {
    w = parseFloat(w, 10);
  }
  if (typeof h === 'string') {
    h = parseFloat(h, 10);
  }
  let hw = w / 2;
  let hh = h / 2;
  let tl = globalToLocal(target, localToGlobal(layer, [-hw, -hh], gRatio), gRatio);
  let tr = globalToLocal(target, localToGlobal(layer, [hw, -hh], gRatio), gRatio);
  let br = globalToLocal(target, localToGlobal(layer, [hw, hh], gRatio), gRatio);
  let bl = globalToLocal(target, localToGlobal(layer, [-hw, hh], gRatio), gRatio);
  let cmin = [Math.min(tl[0], tr[0], bl[0], br[0]), Math.min(tl[1], tr[1], bl[1], br[1])];
  let cmax = [Math.max(tl[0], tr[0], bl[0], br[0]), Math.max(tl[1], tr[1], bl[1], br[1])];
  return vec2.sub([], cmax, cmin);
}

export function getTransformationDelta(initialTransform, transform) {
  return {
    translationDelta: {
      x: initialTransform[12] - transform[12],
      y: initialTransform[13] - transform[13],
    },
    // For scale and rotation, we want going from a small value to a large one to be positive
    scaleDelta: getScale(transform) - getScale(initialTransform),
    rotationDelta: getRotation(transform) - getRotation(initialTransform),
  };
}

/**
 * Convert a 4x4 matrix with bottom-right origin to top-left
 */
export function convertBRtoTL(mat) {
  let t = !mat ? mat4.create() : Array.isArray(mat) ? mat : Object.values(mat);
  t = mat4.clone(t);
  const rotated = rotateMatrix(t, 180); // rotate 180deg around center
  t[12] = 1 - rotated[12]; // flip tx
  t[13] = 1 - rotated[13]; // flip ty
  return t;
}

export function migrateTransform(transform, { x, y, width, height }) {
  transform = convertBRtoTL(transform);
  let s = getScale(transform);
  let r = (getRotation(transform) * DEG) % TAU;
  let adjusted = vec2.cmul([], [x * s, y * s], [Math.cos(r), Math.sin(r)]);
  let position = vec2.add([], transform.slice(12, 14), adjusted);
  return { scale: s, rotation: r, position, bounds: { x: 0, y: 0, width, height } };
}

export function globalToLocal({ rotation, scale, position }, point, globalRatio = 1) {
  if (typeof position[0] === 'string') {
    position[0] = parseFloat(position[0], 10);
  }
  if (typeof position[1] === 'string') {
    position[1] = parseFloat(position[1], 10);
  }
  let scaleY = scale * globalRatio;
  point = [(point[0] - position[0]) / scale, (point[1] - position[1]) / scaleY];
  return vec2.cmul([], point, [Math.cos(rotation), -Math.sin(rotation)]);
}

export function localToGlobal({ rotation, scale, position }, point, globalRatio = 1) {
  if (typeof position[0] === 'string') {
    position[0] = parseFloat(position[0], 10);
  }
  if (typeof position[1] === 'string') {
    position[1] = parseFloat(position[1], 10);
  }
  let scaleY = scale * globalRatio;
  point = vec2.cmul([], point, [Math.cos(rotation), Math.sin(rotation)]);
  return [position[0] + point[0] * scale, position[1] + point[1] * scaleY];
}

export function getBoundingRect({ rotation, scale, position: [x, y], bounds }) {
  let { width: w, height: h } = bounds;
  let cos = Math.cos(rotation);
  let sin = Math.sin(rotation);
  let width = scale * (w * Math.abs(cos) + h * Math.abs(sin));
  let height = scale * (w * Math.abs(sin) + h * Math.abs(cos));
  return { x, y, width, height };
}
